import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trans, useTranslation } from '@clofo/i18n'
import { clofoApi } from '@clofo/data/api'
import { Button } from '@clofo/ui/button'
import { FormInput } from '@clofo/ui/controlled-field'
import { SubmitButton } from '@clofo/ui/submit-button'
import { usePopup } from '@clofo/ui/hooks'

import { getRedirectTo } from '../lib/redirect'
import { SAML_SESSION_STORAGE_KEY, parseSamlSessionData } from '../lib/saml-session'

type RequestEmailFormProps = {
  /** Called when the email's domain is not SSO-enabled and a password is required next. */
  onPasswordRequired: (email: string) => void
}

/**
 * First step of the adaptive login flow. The user enters their email; we probe
 * `/api/v1/saml/generate_auth_url` for the email's domain. A non-empty `url` means the domain is
 * SSO-enabled, so we open the identity provider in a popup and redirect this window to the `next`
 * destination once the popup completes the flow. An empty `url` means password login, so we
 * advance to the password step. Mirrors the legacy login app's `request-email` state.
 */
export const RequestEmailForm = ({ onPasswordRequired }: RequestEmailFormProps) => {
  const { t } = useTranslation()
  const popup = usePopup()
  const [isWaitingForSso, setIsWaitingForSso] = useState(false)

  const schema = z.object({ email: z.email({ error: t('errors.validEmail') }) })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  /**
   * Opens the IdP in a popup and waits for it to post the outcome. After the IdP round-trip the
   * popup renders the backend's `saml_redirect.html`, which writes the session payload to
   * localStorage — same-origin writes fire a `storage` event here in the opener (the mechanism
   * the legacy Ember login app uses; see lib/saml-session.ts). Resolves only when the flow fails
   * (popup blocked, closed early, or the payload carries an error) — on success this window
   * navigates away, so the form stays in its submitting state until then.
   */
  const loginWithSsoPopup = (ssoUrl: string) =>
    new Promise<void>(resolve => {
      setIsWaitingForSso(true)
      const fail = (message: string) => {
        window.removeEventListener('storage', onStorage)
        setIsWaitingForSso(false)
        form.setError('email', { message })
        resolve()
      }
      const onStorage = (event: StorageEvent) => {
        if (event.key !== SAML_SESSION_STORAGE_KEY || !event.newValue) return
        popup.close()
        const result = parseSamlSessionData(event.newValue)
        if (!result.ok) {
          fail(t('login.errors.generic'))
          return
        }
        window.removeEventListener('storage', onStorage)
        window.location.href = getRedirectTo()
      }
      // Clear any leftover payload first — writing a value identical to the stored one would not
      // fire a storage event.
      window.localStorage.setItem(SAML_SESSION_STORAGE_KEY, '')
      window.addEventListener('storage', onStorage)
      popup.open({
        url: ssoUrl,
        name: 'clofo-sso',
        onError: errorType =>
          fail(
            errorType === 'blocked'
              ? t('login.errors.popupBlocked')
              : t('login.errors.popupClosedEarly'),
          ),
      })
    })

  const onSubmit = async ({ email }: Values) => {
    const domain = email.split('@')[1]
    try {
      const url = await clofoApi.auth.generateAuthUrl({ domain })
      if (url) {
        // Carry the login URL's `next` onto the IdP URL so SSO returns the popup to the user's
        // destination. `next` is never sent to generate_auth_url itself.
        const next = window.location.href.match(/next=([^&]*)/)?.[1]
        await loginWithSsoPopup(next ? `${url}${url.includes('?') ? '&' : '?'}next=${next}` : url)
        return
      }
      onPasswordRequired(email)
    } catch {
      form.setError('email', { message: t('login.errors.generic') })
    }
  }

  return (
    <div className='flex flex-col max-w-sm w-full'>
      {isWaitingForSso ? (
        <div className='flex flex-col text-center'>
          <p className='text-secondary-foreground text-xs mb-1'>{t('login.siginAs')}</p>
          <p className='mb-8 font-semibold text-lg'>{form.getValues('email')}</p>
          <p>
            <Trans
              t={t}
              i18nKey='login.popup'
              components={{
                refocus: (
                  <Button
                    type='button'
                    variant='link'
                    onClick={popup.focus}
                    className='inline h-auto p-0 text-sm font-normal underline align-baseline'
                  />
                ),
              }}
            />
          </p>
        </div>
      ) : (
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col'>
          <p className='text-center mb-3'>{t('login.emailLabel')}</p>
          <FormInput
            control={form.control}
            name='email'
            type='email'
            label={t('login.emailPlaceholder')}
            hideLabel
            placeholder={t('login.emailPlaceholder')}
            autoComplete='email'
            autoFocus
            className={{ input: 'h-15' }}
          />
          <SubmitButton
            type='submit'
            size='lg'
            className='mt-6'
            isSubmitting={form.formState.isSubmitting}
          >
            {t('login.next')}
          </SubmitButton>
        </form>
      )}
      {/* Always-mounted status region so the waiting announcement is picked up reliably. */}
      <p role='status' className='sr-only'>
        {isWaitingForSso ? t('login.waitingForLogin') : null}
      </p>
    </div>
  )
}
